import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '@/lib/api';
import { Plus, Search, Edit2, Trash2, Building2 } from 'lucide-react';

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    supplier_name: '',
    supplier_emails: '',
    phone_number: '',
    notes: '',
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await getSuppliers();
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        supplier_emails: formData.supplier_emails
          ? formData.supplier_emails.split(',').map(e => e.trim()).filter(Boolean)
          : [],
      };
      
      if (editingSupplier) {
        await updateSupplier(editingSupplier.supplier_id, data);
        toast.success('Supplier updated successfully');
      } else {
        await createSupplier(data);
        toast.success('Supplier created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplier_name: supplier.supplier_name,
      supplier_emails: supplier.supplier_emails?.join(', ') || '',
      phone_number: supplier.phone_number || '',
      notes: supplier.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    try {
      await deleteSupplier(supplierId);
      toast.success('Supplier deleted successfully');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      supplier_name: '',
      supplier_emails: '',
      phone_number: '',
      notes: '',
    });
  };

  const filteredSuppliers = suppliers.filter((supplier) =>
    supplier.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.supplier_emails?.some(e => e.toLowerCase().includes(searchTerm.toLowerCase())) ||
    supplier.phone_number?.includes(searchTerm)
  );

  return (
    <div className="space-y-8 p-3" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Suppliers
          </h1>
          <p className="text-muted-foreground">Manage your fabric suppliers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow"
              data-testid="add-supplier-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingSupplier ? 'Edit Supplier' : 'New Supplier'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-uppercase">Supplier Name *</Label>
                <Input
                  data-testid="supplier-name-input"
                  value={formData.supplier_name}
                  onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                  placeholder="Enter supplier name"
                  className="h-11 bg-secondary/50"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Email Addresses</Label>
                <Input
                  data-testid="supplier-email-input"
                  value={formData.supplier_emails}
                  onChange={(e) => setFormData({ ...formData, supplier_emails: e.target.value })}
                  placeholder="email1@example.com, email2@example.com"
                  className="h-11 bg-secondary/50"
                />
                <p className="text-xs text-muted-foreground">Separate multiple emails with commas</p>
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Phone Number</Label>
                <Input
                  data-testid="supplier-phone-input"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="Enter phone number"
                  className="h-11 bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-uppercase">Notes</Label>
                <Textarea
                  data-testid="supplier-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="bg-secondary/50 min-h-[80px]"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-supplier-button"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {editingSupplier ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="supplier-search-input"
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11 bg-secondary/50"
        />
      </div>

      {/* Table */}
      <Card className="border-white/10 bg-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="text-lg font-medium mb-1">No suppliers found</h3>
              <p className="text-muted-foreground text-sm">
                {searchTerm ? 'Try a different search term' : 'Add your first supplier to get started'}
              </p>
            </div>
          ) : (
            <Table className="table-luxury">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Emails</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppliers.map((supplier) => (
                  <TableRow key={supplier.supplier_id} className="table-row-hover">
                    <TableCell className="font-medium">{supplier.supplier_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {supplier.supplier_emails?.length > 0 
                        ? supplier.supplier_emails.join(', ') 
                        : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{supplier.phone_number || '—'}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {supplier.notes || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(supplier)}
                          data-testid={`edit-supplier-${supplier.supplier_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(supplier.supplier_id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-supplier-${supplier.supplier_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuppliersPage;
